/**
 * Grade figures for one course, from its plan (/me/courses/{uuid}/plan). Shared by the course page's
 * Grades tab, the Grades page and Certificates so they always show the same numbers. The scores
 * themselves come from the server (plan.grading, StudentProgressService), the same figures staff see.
 */

/** The certificate rules, used when a plan doesn't carry them (older API). */
export const DEFAULT_CRITERIA = {
  attendance_pass: 70,
  watch_percent: 60,
  activities_pass: 70,
  quiz_weight: 40,
  assignment_weight: 60,
  letters: [
    { letter: "A", band: "90–100", points: 95, capstone: "pass" },
    { letter: "B", band: "80–89", points: 85, capstone: "pass" },
    { letter: "C", band: "70–79", points: 75, capstone: "pass" },
    { letter: "D", band: "60–69", points: 65, capstone: "fail" },
    { letter: "F", band: "Below 60", points: 40, capstone: "fail" },
  ],
};

export const CAPSTONE = {
  passed: "Passed",
  submitted: "Submitted",
  redo_required: "Redo required",
  not_submitted: "Not submitted",
  left_out: "Left out of your team's submission",
};

/** Criterion status values (Not started, In progress, Met, Not met) → label and pill style. */
export const CRITERION = {
  not_started: ["open", "Not started"],
  in_progress: ["today", "In progress"],
  met: ["done", "Met"],
  not_met: ["due", "Not met"],
};

export const fmt = (v) => (v == null ? "—" : `${Number.isInteger(v) ? v : v.toFixed(1)}%`);

export function summarize(plan) {
  // Links and feedback for each piece of work, keyed the way grading items are.
  const lessonOf = {};
  const feedback = {};
  plan.weeks.forEach((w) => {
    w.lessons.forEach((l) => { if (l.assignment) { lessonOf[l.assignment.id] = l.id; feedback[`assignment-${l.assignment.id}`] = l.assignment.submission?.feedback; } });
    w.projects.forEach((p) => { feedback[`project-${p.id}`] = p.submission?.feedback; });
  });
  const to = (i) => (i.type === "quiz" ? { quiz: i.uuid } : i.type === "assignment" ? { assignment: lessonOf[i.id] } : { project: i.id });

  const g = fromGrading(plan.grading, plan.criteria, plan.status !== "active", plan.attendance);
  const items = g.items.map((i) => ({ ...i, to: to(i), note: feedback[`${i.type}-${i.id}`] || null }));
  return {
    ...g,
    items,
    projects: plan.weeks.flatMap((w) => w.projects.map((p) => ({ ...p, weekLabel: w.label }))),
  };
}

/**
 * The view the breakdown components take, from a grading block (plan.grading for a student, or a
 * row's grading on the staff pages). While the course runs the score shown is the current one
 * (work past its deadline); once it has ended, the final one (everything, missing work as 0).
 */
export function fromGrading(grading, criteria = DEFAULT_CRITERIA, ended = false, fallbackAttendance = null) {
  criteria = criteria || DEFAULT_CRITERIA;
  ended = grading?.ended ?? ended;
  const items = (grading?.activities.items || []).map((i) => ({
    ...i,
    // What this item adds to the score shown right now.
    counts: ended
      ? (i.points ?? 0)
      : i.past_deadline && i.status !== "submitted" ? (i.points ?? 0) : null,
  }));

  const act = grading?.activities;
  // While the course runs, the current score (work past its deadline). Once it ends, the final score.
  const activities = act ? {
    score: ended ? act.final : act.current,
    breakdown: ended ? act.final_breakdown : act.current_breakdown,
    final: act.final,
    awaiting: act.awaiting_grade,
    status: act.status || "not_started",
    met: !!act.met,
    onTrack: (ended ? act.final : act.current) != null && (ended ? act.final : act.current) >= criteria.activities_pass,
  } : { score: null, breakdown: null, final: null, awaiting: 0, status: "not_started", met: false, onTrack: false };

  const att = grading?.attendance || { percent: null, attended: 0, held: 0, ...fallbackAttendance, live: null, recording: null };
  const work = items.filter((i) => i.type !== "quiz");

  return {
    criteria,
    ended,
    attendance: { ...att, status: att.status || "not_started", met: !!att.met, onTrack: att.percent != null && att.percent >= criteria.attendance_pass },
    activities,
    capstone: grading?.capstone || { status: "not_submitted", grade: null, met: false },
    ready: !!grading?.ready,
    items,
    submitted: work.filter((i) => i.status !== "missing").length,
    workTotal: work.length,
  };
}
